import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContentMaxPriority,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContentMaxPriority,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { itemBaseSchema } from '@/lib/validationSchemas';
import { formatCurrencyInput, parseCurrencyInput } from '@/lib/formatters';
import { Package, Plus, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useInvalidateAllPedidoQueries } from '@/hooks/useInvalidateAllPedidoQueries';
import { useObrigatoriedadesItem } from '@/hooks/useObrigatoriedadesItem';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pedidoId: string;
  observacoesIniciais?: string;
  onSuccess?: () => void;
}

export function CriarItemPedidoModal({ open, onOpenChange, pedidoId, observacoesIniciais, onSuccess }: Props) {
  const [salvando, setSalvando] = useState(false);
  const [adicionarMais, setAdicionarMais] = useState(false);
  const [produtoId, setProdutoId] = useState('');
  const [valorInputText, setValorInputText] = useState('');
  const [valorTotal, setValorTotal] = useState('');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { invalidateAll } = useInvalidateAllPedidoQueries();

  // Buscar produtos
  const { data: produtos, isLoading: loadingProdutos } = useQuery({
    queryKey: ['produtos-ativos'],
    queryFn: async () => {
      const { data } = await supabase
        .from('produtos')
        .select('*')
        .eq('ativo', true)
        .order('nome');
      return data || [];
    },
  });

  // Produto selecionado
  const produtoSelecionado = produtos?.find(p => p.id === produtoId);

  // Buscar obrigatoriedades do produto para detectar valor_total
  const { data: obrigatoriedades } = useObrigatoriedadesItem(
    produtoId || null,
    'negociacao',
    true // Buscar todas para detectar valor_total
  );

  // Detectar se produto usa valor_total
  const produtoUsaValorTotal = obrigatoriedades?.some(
    o => o.obrigatoriedades_catalogo.codigo === 'valor_total'
  ) ?? false;

  // React Hook Form com apenas 4 campos básicos
  const form = useForm({
    resolver: zodResolver(itemBaseSchema),
    defaultValues: {
      produto_id: '',
      quantidade: 1,
      valor_unit: 0,
      plano_nome: '',
    },
  });

  // Resetar ao abrir/fechar
  useEffect(() => {
    if (!open) {
      form.reset();
      setProdutoId('');
      setValorInputText('');
      setValorTotal('');
    }
  }, [open, form]);

  // Atualizar produto_id no form quando muda
  useEffect(() => {
    if (produtoId) {
      form.setValue('produto_id', produtoId);
    }
  }, [produtoId, form]);

  const handleSalvar = async (continuarAdicionando: boolean = false) => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast({
        variant: 'destructive',
        title: 'Campos obrigatórios pendentes',
        description: 'Preencha todos os campos obrigatórios antes de salvar',
      });
      return;
    }

    const data = form.getValues();
    setSalvando(true);
    setAdicionarMais(continuarAdicionando);

    try {
      // Buscar status inicial padrão (AGUARDANDO INTERAÇÃO no COMERCIAL)
      const { data: statusInicial, error: statusError } = await supabase
        .from('status_catalogo')
        .select('id, coluna, quadro')
        .eq('nome', 'AGUARDANDO INTERAÇÃO')
        .eq('quadro', 'COMERCIAL')
        .eq('ativo', true)
        .single();

      if (statusError || !statusInicial) {
        throw new Error('Status inicial não encontrado');
      }

      // Buscar próximo número sequencial
      const { data: itensExistentes } = await supabase
        .from('itens_pedido')
        .select('seq')
        .eq('pedido_id', pedidoId)
        .order('seq', { ascending: false })
        .limit(1);

      const proximoSeq = itensExistentes?.length > 0
        ? (itensExistentes[0].seq || 0) + 1
        : 1;

      // Detectar se produto precisa de campos de linha telefônica
      const nomeNormalizado = (produtoSelecionado?.nome || '').toUpperCase();
      const precisaLinhas = 
        /^PN\b/.test(nomeNormalizado) ||   // PN, PN PF-PJ
        /^PP\b/.test(nomeNormalizado) ||   // PP MÓVEL
        /^TT\b/.test(nomeNormalizado) ||   // TT (Troca Titularidade)
        /^MP\b/.test(nomeNormalizado) ||   // MP
        /^HA\b/.test(nomeNormalizado) ||   // HA (Habilitação)
        nomeNormalizado.includes('FIBRA');

      // Inicializar array de linhas vazio baseado na quantidade
      const quantidade = data.quantidade || 1;
      const linhasIniciais = precisaLinhas 
        ? Array.from({ length: quantidade }, () => ({
            ddd: '',
            linha: '',
            gb: null,
            operadora_doadora: null,
            tipo_chip: null,
          }))
        : null;

      const novoItem: any = {
        pedido_id: pedidoId,
        produto_id: data.produto_id,
        quantidade: data.quantidade,
        valor_unit: data.valor_unit,
        plano_nome: data.plano_nome.trim(),
        familia: produtoSelecionado?.familia || 'Outro',
        faturamento_tipo: produtoSelecionado?.faturamento_tipo || 'NÃO PAGO',
        seq: proximoSeq,
        // ✅ Campos obrigatórios da nova arquitetura
        status_id: statusInicial.id,
        quadro_atual: statusInicial.quadro,
        coluna_atual: statusInicial.coluna,
        entrada_coluna_em: new Date().toISOString(),
        // ✅ Inicializar campos de linha para produtos móveis (igual à proposta)
        linhas_telefone: linhasIniciais,
        observacoes_item: precisaLinhas ? 'ESIM COMO PADRAO!' : null,
      };

      const { error } = await supabase
        .from('itens_pedido')
        .insert([novoItem]);

      if (error) throw error;

      toast({
        title: '✓ Item adicionado!',
        description: continuarAdicionando 
          ? 'Adicione outro item ao pedido'
          : 'Item criado com sucesso',
      });

      invalidateAll();

      if (continuarAdicionando) {
        form.reset();
        setProdutoId('');
        setValorInputText('');
      } else {
        if (onSuccess) onSuccess();
        onOpenChange(false);
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar item',
        description: error.message,
      });
    } finally {
      setSalvando(false);
      setAdicionarMais(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentMaxPriority className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar Item ao Pedido</DialogTitle>
          <DialogDescription>
            {produtoSelecionado 
              ? `${produtoSelecionado.nome} - ${produtoSelecionado.familia}`
              : 'Selecione um produto e preencha as informações básicas'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* ALERTA CONTEXTO */}
          {!produtoId && (
            <Alert>
              <Package className="h-4 w-4" />
              <AlertDescription>
                 Selecione um produto para começar. Campos detalhados podem ser preenchidos depois no card do item.
              </AlertDescription>
            </Alert>
          )}

          {/* SELEÇÃO DE PRODUTO */}
          <div>
            <Label className="font-medium">Produto *</Label>
            <Select value={produtoId} onValueChange={setProdutoId}>
              <SelectTrigger className={form.formState.errors.produto_id ? 'border-destructive' : ''}>
                <SelectValue placeholder="Selecione o produto..." />
              </SelectTrigger>
              <SelectContentMaxPriority>
                {loadingProdutos && (
                  <div className="p-2 text-center text-sm text-muted-foreground">
                    Carregando produtos...
                  </div>
                )}
                {produtos?.map((produto) => (
                  <SelectItem key={produto.id} value={produto.id}>
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span>{produto.nome}</span>
                      <Badge variant="outline" className="text-xs">
                        {produto.familia}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContentMaxPriority>
            </Select>
            {form.formState.errors.produto_id && (
              <p className="text-xs text-destructive mt-1">
                {form.formState.errors.produto_id.message}
              </p>
            )}
          </div>

          {/* CAMPOS BÁSICOS - APENAS APÓS SELECIONAR PRODUTO */}
          {produtoId && (
            <>
              <Separator />
              
              <div className="space-y-4">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                   Informações Básicas
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quantidade *</Label>
                    <Input
                      type="number"
                      {...form.register('quantidade', { valueAsNumber: true })}
                      min={1}
                      className={form.formState.errors.quantidade ? 'border-destructive' : ''}
                      onChange={(e) => {
                        const novaQuantidade = Number(e.target.value);
                        form.setValue('quantidade', novaQuantidade);
                        
                        // Se usa valor_total, recalcular valor_unit
                        if (produtoUsaValorTotal && valorTotal) {
                          const valorTotalNum = parseCurrencyInput(valorTotal);
                          const novoValorUnit = novaQuantidade > 0 ? valorTotalNum / novaQuantidade : 0;
                          form.setValue('valor_unit', novoValorUnit);
                        }
                      }}
                    />
                    {form.formState.errors.quantidade && (
                      <p className="text-xs text-destructive mt-1">
                        {form.formState.errors.quantidade.message}
                      </p>
                    )}
                  </div>

                  {produtoUsaValorTotal ? (
                    <div>
                      <Label>Valor Total *</Label>
                      <Input
                        type="text"
                        placeholder="R$ 0,00"
                        value={valorTotal}
                        onChange={(e) => {
                          const textoDigitado = e.target.value;
                          setValorTotal(textoDigitado);
                          const valorTotalNum = parseCurrencyInput(textoDigitado);
                          const quantidade = form.watch('quantidade') || 1;
                          const valorUnit = quantidade > 0 ? valorTotalNum / quantidade : 0;
                          form.setValue('valor_unit', valorUnit, { shouldValidate: false });
                        }}
                        onBlur={() => {
                          const valorTotalNum = parseCurrencyInput(valorTotal);
                          if (valorTotalNum && valorTotalNum > 0) {
                            const valorFormatado = formatCurrencyInput(valorTotalNum);
                            setValorTotal(valorFormatado);
                            const quantidade = form.watch('quantidade') || 1;
                            const valorUnit = valorTotalNum / quantidade;
                            form.setValue('valor_unit', parseFloat(valorUnit.toFixed(2)), { shouldValidate: true });
                          } else {
                            setValorTotal('');
                            form.setValue('valor_unit', 0);
                          }
                        }}
                        onFocus={() => {
                          const valorUnit = form.watch('valor_unit');
                          const quantidade = form.watch('quantidade') || 1;
                          const valorTotalNum = valorUnit * quantidade;
                          if (valorTotalNum > 0) {
                            setValorTotal(valorTotalNum.toFixed(2).replace('.', ','));
                          }
                        }}
                        className={form.formState.errors.valor_unit ? 'border-destructive' : ''}
                      />
                      {form.formState.errors.valor_unit && (
                        <p className="text-xs text-destructive mt-1">
                          {form.formState.errors.valor_unit.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                         Valor total do pacote (não multiplica pela quantidade)
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Label>Valor Unitário *</Label>
                      <Input
                        type="text"
                        placeholder="R$ 0,00"
                        value={valorInputText}
                        onChange={(e) => {
                          const textoDigitado = e.target.value;
                          setValorInputText(textoDigitado);
                          const valorNumerico = parseCurrencyInput(textoDigitado);
                          form.setValue('valor_unit', valorNumerico, { shouldValidate: false });
                        }}
                        onBlur={() => {
                          const valor = form.watch('valor_unit');
                          if (valor && valor > 0) {
                            const valorFormatado = formatCurrencyInput(valor);
                            setValorInputText(valorFormatado);
                            form.setValue('valor_unit', parseFloat(valor.toFixed(2)), { shouldValidate: true });
                          } else {
                            setValorInputText('');
                            form.setValue('valor_unit', 0);
                          }
                        }}
                        onFocus={() => {
                          const valor = form.watch('valor_unit');
                          if (valor > 0) {
                            setValorInputText(valor.toFixed(2).replace('.', ','));
                          }
                        }}
                        className={form.formState.errors.valor_unit ? 'border-destructive' : ''}
                      />
                      {form.formState.errors.valor_unit && (
                        <p className="text-xs text-destructive mt-1">
                          {form.formState.errors.valor_unit.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Nome do Plano/Serviço *</Label>
                  <Input
                    placeholder="Ex: HA Fresh, 6GB, Aparelho XYZ"
                    {...form.register('plano_nome')}
                    className={form.formState.errors.plano_nome ? 'border-destructive' : ''}
                  />
                  {form.formState.errors.plano_nome && (
                    <p className="text-xs text-destructive mt-1">
                      {form.formState.errors.plano_nome.message}
                    </p>
                  )}
                </div>

                {form.watch('quantidade') && form.watch('valor_unit') && (
                  <div className="bg-muted/30 p-3 rounded-lg">
                    <p className="text-sm font-medium">
                      Subtotal: R$ {(form.watch('quantidade') * form.watch('valor_unit')).toFixed(2)}
                    </p>
                  </div>
                )}

                <Alert className="bg-primary/5 border-primary/40 dark:bg-primary/30 dark:border-primary">
                  <Package className="h-4 w-4 text-primary dark:text-primary" />
                  <AlertDescription className="text-primary dark:text-primary">
                     <strong>Campos detalhados</strong> (DDD, linha, endereço, gestor, etc) podem ser preenchidos depois no card expandido do item com auto-save.
                  </AlertDescription>
                </Alert>
              </div>
            </>
          )}

        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={salvando}
          >
            Cancelar
          </Button>
          
          {produtoId && (
            <>
              <Button
                variant="outline"
                onClick={() => handleSalvar(true)}
                disabled={salvando}
              >
                <Plus className="w-4 h-4 mr-2" />
                {salvando && adicionarMais ? 'Salvando...' : 'Adicionar e Novo'}
              </Button>
              
              <Button
                onClick={() => handleSalvar(false)}
                disabled={salvando}
              >
                <Check className="w-4 h-4 mr-2" />
                {salvando && !adicionarMais ? 'Salvando...' : 'Adicionar Item'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContentMaxPriority>
    </Dialog>
  );
}
